import React, {useEffect, useMemo, useRef, useState, useCallback } from 'react'
import JobsSearchBar from '../../components/jobs/JobsSearchBar'
import { AdvertisingStatus, JobStatus, JobSearchBy } from '../../data/enums/jobEnums'
import JobsTable from '../../components/jobs/JobsTable'
import JobsService, { JobData } from '../../api/JobsService'
import Spinner from '../../components/Spinner'
import { usePersistedJobsState } from '../../hooks/usePersistedJobsState'
import { setWithExpiry, getWithExpiry } from '../../utils/sessionStorage'
import { normalizeAdvertisingStatus } from '../../utils/jobTransformations'
import QuestionnaireAttachmentDrawer from '../../components/jobs/QuestionnaireAttachmentDrawer'
import { getInterviewsByJobId } from '../../api/ApplicationService'
import { useJobSearchApi } from '../../hooks/useJobSearchApi'

const JOBS_PER_PAGE = 20;

interface IJobsPageProps {
    isMobile: boolean;  
    jobs: JobData[];
    isLoadingJobs: boolean;
    onRefresh?: () => void;
    questionnaires: any;
    jobQuestionnaireMap: any;
    jobsWithQuestionnaires: any;
    isLoadingQuestionnaires?: boolean;
    loadingJobIds?: Set<number>;
    onRefreshSingleJob?: (jobId: number) => Promise<void>;
    initialJobId?: number | null;
    initialJobIdSource?: 'url' | 'interview-click' | null;
    onInterviewCountClick?: (jobId: number) => void;
    activeTab?: string;
    onJobsDiscovered?: (jobs: JobData[]) => void;
    paginationMeta?: { total: number; totalPages: number } | null;
    // VI-579 follow-up (Bug 1c): forwarded to QuestionnaireAttachmentDrawer so the
    // Questionnaires-tab table updates its cached "Jobs Attached" count optimistically
    // when the user attaches/detaches a questionnaire from this Jobs-tab UI.
    onActiveJobsCountChange?: (
        questionnaireId: string,
        change: { delta?: number; absolute?: number }
    ) => void;
    relationshipManagerEmail?: string | null;
}

const JobsPage = ({ 
    isMobile, 
    jobs, 
    isLoadingJobs,
    onRefresh,
    questionnaires,
    jobQuestionnaireMap,
    jobsWithQuestionnaires,
    isLoadingQuestionnaires,
    loadingJobIds = new Set(),
    onRefreshSingleJob,
    initialJobId,
    initialJobIdSource,
    onInterviewCountClick,
    activeTab,
    onJobsDiscovered,
    paginationMeta,
    onActiveJobsCountChange,
    relationshipManagerEmail,
  }: IJobsPageProps) => {

    // Use persisted state for filters
    const {
        selectedStatus,
        setSelectedStatus,
        searchTerm,
        setSearchTerm,
        advertisingStatusFilter,
    } = usePersistedJobsState();

    // PAGINATION STATE
    const [currentPage, setCurrentPage] = useState(0);
    const [paginatedJobs, setPaginatedJobs] = useState<JobData[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const pageCache = useRef<Map<number, JobData[]>>(new Map());

    // Sync total count from parent and recalculate totalPages for our page size
    useEffect(() => {
      if (paginationMeta) {
        setTotalCount(paginationMeta.total);
        setTotalPages(Math.ceil(paginationMeta.total / JOBS_PER_PAGE));
      }
    }, [paginationMeta]);

    // Fetch page 0 with our own page size on mount
    const hasFetchedInitialPage = useRef(false);
    useEffect(() => {
      if (hasFetchedInitialPage.current || !paginationMeta) return;
      hasFetchedInitialPage.current = true;

      (async () => {
        setIsLoadingPage(true);
        try {
          const result = await JobsService.getJobsPaginated(undefined, 0, JOBS_PER_PAGE, true);
          pageCache.current.set(0, result.jobs);
          setPaginatedJobs(result.jobs);
          setTotalPages(Math.ceil(result.total / JOBS_PER_PAGE));
          setTotalCount(result.total);
        } catch (error: unknown) {
          console.error('Error fetching initial jobs page:', error);
          if (jobs.length > 0) {
            setPaginatedJobs(jobs);
            pageCache.current.set(0, jobs);
          }
        } finally {
          setIsLoadingPage(false);
        }
      })();
    }, [paginationMeta, jobs]);

    const handlePageChange = useCallback(async (page: number) => {
      setCurrentPage(page);

      const cached = pageCache.current.get(page);
      if (cached) {
        setPaginatedJobs(cached);
        return;
      }

      setIsLoadingPage(true);
      try {
        const result = await JobsService.getJobsPaginated(undefined, page, JOBS_PER_PAGE, true);
        pageCache.current.set(page, result.jobs);
        setPaginatedJobs(result.jobs);
        setTotalPages(Math.ceil(result.total / JOBS_PER_PAGE));
        setTotalCount(result.total);
        onJobsDiscovered?.(result.jobs);
      } catch (error: unknown) {
        console.error('Error fetching jobs page:', error);
      } finally {
        setIsLoadingPage(false);
      }
    }, [onJobsDiscovered]);

    // State to store interview counts per job
    const [jobInterviewCounts, setJobInterviewCounts] = React.useState<Map<number, number>>(() => {
        // Initialize from cache if available
        const cached = getWithExpiry('jobInterviewCounts');
        if (cached?.counts) {
            console.log('✅ Loaded interview counts from cache:', Object.keys(cached.counts).length, 'jobs');
            return new Map(Object.entries(cached.counts).map(([k, v]) => [Number(k), Number(v)]));
        }
        return new Map();
    });

    // Helper to get cached count for a specific job
    const getCachedCount = useCallback((jobId: number): number | null => {
        const cached = getWithExpiry('jobInterviewCounts');
        if (cached?.counts && cached.counts[jobId] !== undefined) {
            return cached.counts[jobId];
        }
        return null;
    }, []);

    // Helper to cache a single job's count
    const cacheJobCount = useCallback((jobId: number, count: number) => {
        const cached = getWithExpiry('jobInterviewCounts') || { counts: {} };
        cached.counts[jobId] = count;
        setWithExpiry('jobInterviewCounts', cached, 5 * 60 * 1000);
    }, []);

    // Initialize questionnaires from cache if available (only check cache once)
    const cachedQuestionnairesRef = useRef<any[] | null>(null);
    const cachedQuestionnaires = useMemo(() => {
        if (questionnaires?.length > 0) {
            cachedQuestionnairesRef.current = questionnaires;
            return questionnaires;
        }
        // Only check cache if we haven't loaded from props yet
        if (cachedQuestionnairesRef.current === null) {
            const cached = getWithExpiry('cachedQuestionnaires');
            cachedQuestionnairesRef.current = cached || [];
        }
        return cachedQuestionnairesRef.current;
    }, [questionnaires]);
    
    // Initialize jobQuestionnaireMap from cache if available (only check cache once)
    const cachedJobQuestionnaireMapRef = useRef<Map<any, any> | null>(null);
    const cachedJobQuestionnaireMap = useMemo(() => {
        if (jobQuestionnaireMap?.size > 0) {
            cachedJobQuestionnaireMapRef.current = jobQuestionnaireMap;
            return jobQuestionnaireMap;
        }
        // Only check cache if we haven't loaded from props yet
        if (cachedJobQuestionnaireMapRef.current === null) {
            const cached = getWithExpiry('cachedJobQuestionnaireMap');
            cachedJobQuestionnaireMapRef.current = cached ? new Map(Object.entries(cached)) : new Map();
        }
        return cachedJobQuestionnaireMapRef.current;
    }, [jobQuestionnaireMap]);
    
    const [isLoadingInterviews, setIsLoadingInterviews] = React.useState(false);
    const hasFetchedRef = useRef(false);

    // Track previous values to prevent unnecessary re-fetches
    const prevJobsLengthRef = useRef(0);
    const prevJobsWithQuestionnairesRef = useRef<Set<number>>(new Set());
    
    // Fetch all interviews for jobs with questionnaires
    useEffect(() => {
        const fetchAllInterviews = async () => {
            if (!jobs.length || !jobQuestionnaireMap || jobQuestionnaireMap.size === 0) {
                return;
            }

            const jobsWithQuestionnairesArray = Array.from(jobsWithQuestionnaires) as number[];
            
            // ALWAYS check cache first, regardless of hasFetchedRef
            const cachedCounts: Map<number, number> = new Map();
            const jobsToFetch: number[] = [];

            jobsWithQuestionnairesArray.forEach(jobId => {
                const cachedCount = getCachedCount(jobId);
                if (cachedCount !== null) {
                    cachedCounts.set(jobId, cachedCount);
                } else {
                    jobsToFetch.push(jobId);
                }
            });

            console.log(`📊 Counts: ${cachedCounts.size} cached, ${jobsToFetch.length} to fetch`);

            // Set cached counts immediately
            if (cachedCounts.size > 0) {
                setJobInterviewCounts(cachedCounts);
            }

            // If all are cached, we're done - no need to fetch
            if (jobsToFetch.length === 0) {
                console.log('✅ All interview counts loaded from cache - no API calls needed');
                hasFetchedRef.current = true;
                prevJobsLengthRef.current = jobs.length;
                prevJobsWithQuestionnairesRef.current = new Set(jobsWithQuestionnaires);
                return;
            }

            // Check if we already fetched these specific jobs
            const alreadyFetchedAll = hasFetchedRef.current && 
                jobsToFetch.every(jobId => prevJobsWithQuestionnairesRef.current.has(jobId));
            
            if (alreadyFetchedAll) {
                console.log('⏭️ Skipping fetch - these jobs were already fetched');
                return;
            }

            hasFetchedRef.current = true;
            setIsLoadingInterviews(true);
            console.log(`🔄 Fetching interview counts for ${jobsToFetch.length} NEW jobs...`);

            try {
                const interviewPromises = jobsToFetch.map(async (jobId: any) => {
                    try {
                        // Use the new job-specific endpoint
                        const response = await getInterviewsByJobId(
                            jobId,
                            { page: 1, limit: 1 }, // Only need count, not data
                            "Completed"
                        );

                        const interviewCount = response?.total ?? 0;
                        console.log(`✅ Job ${jobId}: ${interviewCount} completed interviews`);
                        
                        // Cache this job's count immediately
                        cacheJobCount(jobId, interviewCount);
                        
                        return { jobId, count: interviewCount };
                    } catch (error: any) {
                        // Cache 0 count for 404s to avoid repeated failed requests
                        if (error?.response?.status === 404) {
                            console.log(`ℹ️ Job ${jobId}: No interviews found (404) - caching 0`);
                            cacheJobCount(jobId, 0);
                        } else {
                            console.error(`❌ Error fetching interviews for job ${jobId}:`, error);
                        }
                        return { jobId, count: 0 };
                    }
                });

                const results = await Promise.all(interviewPromises);
                const newCounts = new Map(results.map(({ jobId, count }) => [jobId, count]));
                
                // Merge cached and new counts
                const allCounts = new Map<number, number>(cachedCounts);
                newCounts.forEach((count, jobId) => {
                    allCounts.set(jobId, count);
                });
                setJobInterviewCounts(allCounts);

                console.log(`✅ Interview counts updated: ${allCounts.size} total jobs`);
                
                // Update refs
                prevJobsLengthRef.current = jobs.length;
                prevJobsWithQuestionnairesRef.current = new Set(jobsWithQuestionnaires);
                
            } catch (error) {
                console.error('❌ Error fetching interviews:', error);
            } finally {
                setIsLoadingInterviews(false);
            }
        };

        fetchAllInterviews();
    }, [jobs.length, jobQuestionnaireMap, jobsWithQuestionnaires]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: getCachedCount and cacheJobCount are excluded from deps as they're stable and read from sessionStorage

    // Helper function to get interview count for a job
    const getInterviewCount = (jobId: number): number => {
        return jobInterviewCounts.get(jobId) || 0;
    };

    // State for QuestionnaireAttachmentDrawer
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedJobForDrawer, setSelectedJobForDrawer] = useState<JobData | null>(null);
    
    const hasProcessedInitialJobId = useRef<number | null>(null);

    // Handle initial job selection from URL parameter
    // ONLY process if source is explicitly 'url' (not 'interview-click')
    useEffect(() => {
        console.log('🔍 JobsPage drawer check:', {
            initialJobId,
            initialJobIdSource,
            activeTab,
            jobsLength: jobs.length,
            isLoadingJobs,
            hasProcessed: hasProcessedInitialJobId.current,
            loadingThisJob: initialJobId ? loadingJobIds.has(initialJobId) : false
        });

        // Detailed condition checking for debugging
        const conditions = {
            hasInitialJobId: !!initialJobId,
            isSourceUrl: initialJobIdSource === 'url',
            isJobsTab: activeTab === 'jobs',
            hasJobs: jobs.length > 0,
            notLoading: !isLoadingJobs,
            notLoadingThisJob: initialJobId ? !loadingJobIds.has(initialJobId) : false,
            notProcessedYet: hasProcessedInitialJobId.current !== initialJobId
        };
        
        console.log('📋 Condition details:', conditions);
        
        const allConditionsMet = Object.values(conditions).every(v => v === true);
        console.log('✓ All conditions met?', allConditionsMet);

        if (
            initialJobId && 
            initialJobIdSource === 'url' && // EXPLICIT CHECK: Only open drawer if coming from URL
            activeTab === 'jobs' && // Must be on jobs tab
            jobs.length > 0 && 
            !isLoadingJobs &&
            !loadingJobIds.has(initialJobId) && 
            hasProcessedInitialJobId.current !== initialJobId // Only process if we haven't processed THIS specific jobId
        ) {
            const jobToSelect = jobs.find(job => job.id === initialJobId);
            if (jobToSelect) {
                console.log('✅ Opening drawer from URL parameter:', jobToSelect.title);
                // Set search term
                setSearchTerm(initialJobId.toString());
                // Open drawer
                setSelectedJobForDrawer(jobToSelect);
                setIsDrawerOpen(true);
                // Mark THIS specific jobId as processed
                hasProcessedInitialJobId.current = initialJobId;
            } else {
                console.warn('⚠️ Job with ID', initialJobId, 'not found in jobs list');
            }
        } else if (initialJobIdSource === 'interview-click') {
            console.log('🚫 Skipping drawer open - source is interview-click, not url');
        } else if (initialJobIdSource === 'url' && !allConditionsMet) {
            console.log('⏳ Waiting for conditions to be met for URL drawer open...');
        }
    }, [initialJobId, initialJobIdSource, activeTab, jobs, isLoadingJobs, loadingJobIds, setSearchTerm]);
    
    // Reset processed flag when source changes or tab changes
    useEffect(() => {
        if (initialJobIdSource !== 'url' || activeTab !== 'jobs') {
            hasProcessedInitialJobId.current = null;
        }
    }, [initialJobIdSource, activeTab]);

    const handleAttach = (jobId: number) => {
        const job = jobs.find(j => j.id === jobId);
        if (job) {
            setSelectedJobForDrawer(job);
            setIsDrawerOpen(true);
        }
    };

    const handleManageJobQuestionnaire = (jobId: number) => {
        const job = jobs.find(j => j.id === jobId);
        if (job) {
            setSelectedJobForDrawer(job);
            setIsDrawerOpen(true);
        }
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
    };

    const handleQuestionnaireAttached = async () => {
        // Refresh only the specific job's questionnaire status
        if (onRefreshSingleJob && selectedJobForDrawer?.id) {
            await onRefreshSingleJob(selectedJobForDrawer.id);
        }
        // // Also clear the interview counts cache so it refetches
        // setJobInterviewCounts(new Map());
        // hasFetchedRef.current = false;
    };

    // memoized lookup map (only recomputes when questionnaires change)
    const questionnaireNameMap = useMemo(() => {
        if (!cachedQuestionnaires) return new Map<string, string>();
        
        return new Map(
            cachedQuestionnaires.map((q: any) => [
                q.questionnaireId, 
                q.questionnaireName
            ])
        );
    }, [cachedQuestionnaires]);

    // fast O(1) lookup - replaces the slow .find() version (memoized to prevent re-creation)
    const getQuestionnaireName = useCallback((jobId: number): string => {
        const questionnaireId = cachedJobQuestionnaireMap?.get(jobId);
        if (!questionnaireId) return '–';
        
        return questionnaireNameMap.get(questionnaireId) as string || '–';
    }, [cachedJobQuestionnaireMap, questionnaireNameMap]);

    // VI-375: search paginates server-side at the same page size as browse.
    const searchApi = useJobSearchApi(onJobsDiscovered, JOBS_PER_PAGE);

    // Handle search (memoized to prevent re-creation)
    const handleSearch = useCallback((term: string, searchBy: JobSearchBy) => {
        setSearchTerm(term);

        if (term?.trim()) {
            // Field-scoped search (Job Title / Job ID / Req ID) resolved server-side.
            searchApi.searchByKeyword(term.trim(), searchBy);
        } else {
            searchApi.clearSearch();
            setCurrentPage(0);
            const firstPage = pageCache.current.get(0);
            if (firstPage) setPaginatedJobs(firstPage);
        }
    }, [searchApi, setSearchTerm]);

    // VI-375 (OBS-3): Core's /search/count can report more jobs than /search
    // actually returns, so a last page derived from that count may come back
    // empty. If we ever land on an empty search page past the first, step back
    // one page so the user never sits on a phantom empty page.
    useEffect(() => {
        if (
            searchApi.hasSearched &&
            !searchApi.isSearching &&
            !searchApi.searchError &&
            searchApi.searchResults.length === 0 &&
            searchApi.searchPage > 0
        ) {
            searchApi.goToSearchPage(searchApi.searchPage - 1);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchApi.hasSearched, searchApi.isSearching, searchApi.searchError, searchApi.searchResults.length, searchApi.searchPage]);

    // Filter jobs by Job Status (Open/Closed) and Search
    const filteredJobs = useMemo(() => {
        let allJobs: JobData[];

        if (searchApi.hasSearched) {
            // VI-375: the field-scoped search (Job Title / Job ID / Req ID) is
            // resolved server-side, so trust the returned page as-is (de-dupe
            // only). We deliberately do NOT re-broaden with locally cached jobs
            // or re-narrow with a client substring match -- either would fight
            // the server's field scoping and exact-match semantics.
            const seenIds = new Set<number>();
            allJobs = searchApi.searchResults.filter(job => {
                if (seenIds.has(job.id)) return false;
                seenIds.add(job.id);
                return true;
            });
        } else {
            allJobs = paginatedJobs.length > 0 ? paginatedJobs : jobs;
        }

        let filtered = allJobs;

        // Filter by Job Status (Open/Closed). VI-885 (Finding #1): the status
        // toggle applies to BROWSE only, never to search. Search is paginated
        // server-side, so filtering its pages client-side left empty pages the
        // pager still offered (and Core's /search/count ignores status anyway).
        // A search (any mode) returns all matching statuses; the toggle is inert
        // while searching. This also unifies the exact-mode bypass (Finding #3).
        if (!searchApi.hasSearched) {
            filtered = filtered.filter(job => {
                if (selectedStatus === JobStatus.ALL) return true;
                if (selectedStatus === JobStatus.OPEN) return job.status === 'Open';
                if (selectedStatus === JobStatus.CLOSED) return job.status === 'Closed';
                return true;
            });
        }

        // Filter by Advertising Status
        if (advertisingStatusFilter !== AdvertisingStatus.ALL) {
            filtered = filtered.filter(job => {
                const status = job.advertisingStatus
                    ? (job.advertisingStatus as AdvertisingStatus)
                    : normalizeAdvertisingStatus(job.jobOrigin);
                return status === advertisingStatusFilter;
            });
        }

        // Browse view only: local substring filter over the current page (e.g.
        // the URL-driven job highlight that sets searchTerm without running a
        // server search). Skipped while a server search is active so it doesn't
        // override the field-scoped results.
        if (!searchApi.hasSearched && searchTerm.trim()) {
            const normalizedSearch = searchTerm.toLowerCase().replace(/\s+/g, '');

            filtered = filtered.filter(job => {
                const locationStrings: string[] = [];

                if (job.locations && job.locations.length > 0) {
                    job.locations.forEach(location => {
                        if (location.city) locationStrings.push(location.city);
                        if (location.region) locationStrings.push(location.region);
                        if (location.country) locationStrings.push(location.country);
                        if (location.postalCode) locationStrings.push(location.postalCode);

                        if (location.city && location.region) {
                            locationStrings.push(`${location.city}, ${location.region}`);
                            locationStrings.push(`${location.city},${location.region}`);
                        }

                        if (location.city && location.region && location.country) {
                            locationStrings.push(`${location.city}, ${location.region}, ${location.country}`);
                            locationStrings.push(`${location.city}, ${location.region},${location.country}`);
                        }
                    });
                }

                const searchableText = [
                    job.title,
                    job.requisitionName,
                    job.id?.toString(),
                    getQuestionnaireName(job.id) !== '–' ? getQuestionnaireName(job.id) : '',
                    ...locationStrings
                ].filter(Boolean).join(' ').toLowerCase().replace(/\s+/g, '');

                return searchableText.includes(normalizedSearch);
            });
        }

        return filtered;
    }, [jobs, paginatedJobs, searchApi.searchResults, searchApi.hasSearched, selectedStatus, advertisingStatusFilter, searchTerm]);

    // VI-375 (OBS-3): clamp the search pager to actual results. If the current
    // page came back full (=== page size) there may be more, so trust Core's
    // count for the page total; if it came back short, this IS the last page, so
    // cap the total there -- immune to Core's inflated /search/count.
    const searchPageFull = searchApi.searchResults.length === JOBS_PER_PAGE;
    const searchTotalPages = searchApi.hasSearched
        ? (searchPageFull ? Math.ceil(searchApi.searchTotal / JOBS_PER_PAGE) : searchApi.searchPage + 1)
        : 0;

  return (
    <div 
        style={{    
            marginTop: '2rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
        }}
    >
        {(
            isLoadingJobs
            // Note: isLoadingQuestionnaires and isLoadingInterviews are intentionally NOT included
            // Questionnaire checks show spinner on individual buttons
            // Interview counts load in the background
        ) ? (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                minHeight: '2rem',
                width: '100%',
                marginBottom: '2rem',
              }}>
                <Spinner size={isMobile ? 'md' : 'xl'} />
            </div>
        ) : 
        (
            <>
                <JobsSearchBar isMobile={isMobile} onSearch={handleSearch} initialSearchTerm={searchTerm} />
                <div>
                    <JobStatusFilter
                        selectedStatus={selectedStatus}
                        setSelectedStatus={setSelectedStatus}
                        statusFilteredJobs={filteredJobs}
                        pageJobCount={paginatedJobs.length}
                        isLoading={isLoadingPage || searchApi.isSearching}
                        isSearching={searchApi.hasSearched}
                        disabled={searchApi.hasSearched}
                    />
                    {jobs.length === 0 && (
                        <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        minHeight: '2rem',
                        width: '100%',
                        marginTop: '2rem',
                    }}>
                        <p>No jobs found.</p>
                    </div>
                    )}

                    {jobs.length > 0 && filteredJobs.length === 0 && !searchApi.isSearching && !searchApi.searchError && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '2rem',
                            width: '100%',
                            marginTop: '2rem',
                        }}>
                            <p>No jobs found matching the selected filters.</p>
                        </div>
                    )}

                    {/* VI-885 (Finding #2): a failed search shows an error + retry,
                        not the same "No jobs found" a real zero-result search shows. */}
                    {searchApi.searchError && !searchApi.isSearching && (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '0.75rem',
                            minHeight: '2rem',
                            width: '100%',
                            marginTop: '2rem',
                        }}>
                            <p style={{ marginBottom: 0 }}>Something went wrong while searching. Please check your connection and try again.</p>
                            <button type="button" className="btn btn-primary" onClick={() => searchApi.retrySearch()}>
                                Retry
                            </button>
                        </div>
                    )}

                    {(searchApi.isSearching || isLoadingPage) && (
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            minHeight: '200px',
                            width: '100%',
                            marginTop: '2rem',
                        }}>
                            <Spinner size="xl"/>
                        </div>
                    )}

                    {!isLoadingPage && !searchApi.isSearching && jobs.length > 0 && filteredJobs.length > 0 && (
                        <JobsTable
                            isMobile={isMobile}
                            jobs={filteredJobs}
                            onAttach={handleAttach}
                            onManage={handleManageJobQuestionnaire}
                            getQuestionnaireName={getQuestionnaireName}
                            jobsWithQuestionnaires={jobsWithQuestionnaires}
                            getInterviewCount={getInterviewCount}
                            isLoadingInterviews={isLoadingInterviews}
                            isLoadingQuestionnaires={isLoadingQuestionnaires}
                            loadingJobIds={loadingJobIds}
                            onInterviewCountClick={onInterviewCountClick}
                            relationshipManagerEmail={relationshipManagerEmail}
                        />
                    )}

                    {/* Browse pagination (no active search) */}
                    {!searchApi.hasSearched && totalPages > 1 && (
                        <JobsPagination
                            currentPage={currentPage + 1}
                            totalPages={totalPages}
                            onPageChange={(page) => handlePageChange(page - 1)}
                        />
                    )}

                    {/* VI-375: server-side pagination for field-scoped search results.
                        (OBS-3) totalPages is clamped by actual page fullness, so a short
                        last page can't be paged past even when Core's count overcounts. */}
                    {searchApi.hasSearched && !searchApi.searchError && searchTotalPages > 1 && (
                        <JobsPagination
                            currentPage={searchApi.searchPage + 1}
                            totalPages={searchTotalPages}
                            onPageChange={(page) => searchApi.goToSearchPage(page - 1)}
                        />
                    )}
                </div>
            </>
        )}

        {/* Questionnaire Attachment Drawer - Always rendered for smooth transitions */}
        <QuestionnaireAttachmentDrawer
            isOpen={isDrawerOpen}
            onClose={handleDrawerClose}
            jobId={selectedJobForDrawer?.id || 0}
            jobTitle={selectedJobForDrawer?.title || ''}
            currentQuestionnaireId={cachedJobQuestionnaireMap?.get(selectedJobForDrawer?.id || 0) || null}
            onQuestionnaireAttached={handleQuestionnaireAttached}
            isLoadingCurrentQuestionnaire={selectedJobForDrawer?.id ? loadingJobIds.has(selectedJobForDrawer.id) : false}
            onActiveJobsCountChange={onActiveJobsCountChange}
        />
    </div>
  )
}

interface IJobStatusFilterProps {
    selectedStatus: JobStatus;
    setSelectedStatus: (status: JobStatus) => void;
    statusFilteredJobs: JobData[];
    pageJobCount?: number;
    isLoading?: boolean;
    isSearching?: boolean;
    disabled?: boolean;
}

const JobStatusFilter = ({
    selectedStatus,
    setSelectedStatus,
    statusFilteredJobs,
    pageJobCount = 0,
    isLoading = false,
    isSearching = false,
    disabled = false
}: IJobStatusFilterProps) => {
    const isSmallerDevices = window.innerWidth <= 375; // smaller then normal mobile (i.e galaxy z fold 5, iphone SE)
    // VI-885 (Finding #1): the status toggle filters browsing only, so it is
    // disabled while a search is active (search returns all matching statuses).
    const disabledStyle = disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined;
    const disabledTitle = disabled ? 'Status filter applies to browsing, not to search results' : undefined;
    return (
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginTop: '1rem',
          justifyContent: 'flex-start',
          alignItems: 'center',
        //   backgroundColor: 'red',
        }}>
            <div style={{ display: 'flex', gap: '0.5rem' }} title={disabledTitle}>
                <button
                    type="button"
                    className={selectedStatus === JobStatus.ALL ? "btn btn-primary" : "btn"}
                    onClick={() => setSelectedStatus(JobStatus.ALL)}
                    disabled={disabled}
                    style={disabledStyle}
                >
                    {JobStatus.ALL}
                </button>
                <button
                    type="button"
                    className={selectedStatus === JobStatus.OPEN ? "btn btn-primary" : "btn"}
                    onClick={() => setSelectedStatus(JobStatus.OPEN)}
                    disabled={disabled}
                    style={disabledStyle}
                >
                    {JobStatus.OPEN}
                </button>
                <button
                    type="button"
                    className={selectedStatus === JobStatus.CLOSED ? "btn btn-primary" : "btn"}
                    onClick={() => setSelectedStatus(JobStatus.CLOSED)}
                    disabled={disabled}
                    style={disabledStyle}
                >
                    {JobStatus.CLOSED}
                </button>
          </div>
          {!isLoading && (
            <div style={{ marginLeft: 'auto' }}>
              <p style={{ marginBottom: 0, fontSize: isSmallerDevices ? '0.8rem' : '0.9rem' }}>
                {isSearching
                  ? `Showing ${statusFilteredJobs.length} result${statusFilteredJobs.length !== 1 ? 's' : ''}`
                  : `Showing ${statusFilteredJobs.length} of ${pageJobCount || statusFilteredJobs.length} jobs on this page`
                }
              </p>
            </div>
          )}
        </div>
      )
}

interface IJobsPaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
}

const JobsPagination = ({ currentPage, totalPages, onPageChange }: IJobsPaginationProps) => {
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const pages: number[] = [];
    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    return (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem', marginBottom: '1rem' }}>
            <ul className="pagination mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <a
                        className="page-link"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1) onPageChange(currentPage - 1);
                        }}
                    >
                        <i className="fal fa-chevron-left"></i>
                    </a>
                </li>
                {pages.map((page) => (
                    <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                        <a
                            className="page-link"
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                onPageChange(page);
                            }}
                        >
                            {page}
                        </a>
                    </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <a
                        className="page-link"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages) onPageChange(currentPage + 1);
                        }}
                    >
                        <i className="fal fa-chevron-right"></i>
                    </a>
                </li>
            </ul>
        </div>
    );
};

export default JobsPage;